import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { getCleanHTML } from 'shared/lib/getCleanHTML'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Paper } from 'shared/ui/paper'

import s from './styles.module.scss'

interface DescriptionSectionProps {
  description: string
}

const LINES_TO_SHOW = 3

export const DescriptionSection = ({
  description,
}: DescriptionSectionProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [showButton, setShowButton] = useState(false)
  const descriptionRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (!descriptionRef.current) return

    const element = descriptionRef.current

    requestAnimationFrame(() => {
      if (!element) return

      const wasCollapsed = element.classList.contains(s.collapsed)
      element.classList.remove(s.collapsed)

      const fullHeight = element.scrollHeight
      const computedStyle = window.getComputedStyle(element)
      const lineHeight =
        parseFloat(computedStyle.lineHeight) ||
        parseFloat(computedStyle.fontSize) * 1.5
      const maxHeight = lineHeight * LINES_TO_SHOW

      if (wasCollapsed) {
        element.classList.add(s.collapsed)
      }

      setShowButton(fullHeight > maxHeight)
    })
  }, [description])

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Paper
      padding={[12, 14]}
      radius="16"
    >
      <h2 className={clsx('bold-14', s.sectionTitle)}>
        {t('kyrgyzstan_block.detail.description')}
      </h2>
      <p
        ref={descriptionRef}
        className={clsx('medium-14', s.description, {
          [s.collapsed]: !isExpanded,
        })}
        dangerouslySetInnerHTML={{ __html: getCleanHTML(description) }}
      />
      {showButton && (
        <button
          type="button"
          className={clsx('semi-bold-14', s.moreButton)}
          onClick={handleToggle}
          aria-label={
            isExpanded
              ? t('kyrgyzstan_block.detail.less')
              : t('kyrgyzstan_block.detail.more')
          }
        >
          <AppIcon
            id={AllIcons.ARROW_BOTTOM}
            width={24}
            height={24}
            className={clsx({ [s.rotated]: isExpanded })}
          />
          {isExpanded
            ? t('kyrgyzstan_block.detail.less')
            : t('kyrgyzstan_block.detail.more')}
        </button>
      )}
    </Paper>
  )
}
